import React, { useState, useEffect, useRef } from "react";
import {
  Bug,
  Package,
  MapPin,
  Phone,
  User,
  CheckCircle,
  Trash2,
  MessageSquare,
  ArrowLeft,
  Megaphone,
  Plus,
  X,
  ImagePlus,
  Loader2,
  QrCode,
  Download,
} from "lucide-react";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { QRCodeCanvas } from "qrcode.react"; // 🚀 Імпорт для генерації PNG
import { db } from "../firebase";

export default function Admin({ onClose }: { onClose?: () => void }) {
  const [activeTab, setActiveTab] = useState<"orders" | "bugs" | "ads">("orders");
  const [orders, setOrders] = useState<any[]>([]);
  const [bugs, setBugs] = useState<any[]>([]);
  const [ads, setAds] = useState<any[]>([]);

  const [brandName, setBrandName] = useState("");
  const [adText, setAdText] = useState("");
  const [targetLink, setTargetLink] = useState("");
  const [actionText, setActionText] = useState("Замовити зараз");
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<"image" | "video" | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const unsubOrders = onSnapshot(
      query(collection(db, "tag_orders"), orderBy("createdAt", "desc")),
      (snap) => {
        setOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      }
    );

    const unsubBugs = onSnapshot(
      query(collection(db, "feedback"), orderBy("createdAt", "desc")),
      (snap) => {
        setBugs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      }
    );

    const unsubAds = onSnapshot(
      query(collection(db, "posts"), where("isSponsored", "==", true)),
      (snap) => {
        setAds(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      }
    );

    return () => {
      unsubOrders();
      unsubBugs();
      unsubAds();
    };
  }, []);

  const markOrderAsDone = async (orderId: string) => {
    if (window.confirm("Позначити замовлення як відправлене?")) {
      await updateDoc(doc(db, "tag_orders", orderId), { status: "completed" });
    }
  };

  const deleteBug = async (bugId: string) => {
    if (window.confirm("Видалити цей відгук/баг?")) {
      await deleteDoc(doc(db, "feedback", bugId));
    }
  };

  const deleteAd = async (adId: string) => {
    if (window.confirm("Видалити цю рекламу зі Стрічки навсегда?")) {
      await deleteDoc(doc(db, "posts", adId));
    }
  };

  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMediaFile(file);
    setMediaPreview(URL.createObjectURL(file));
    setMediaType(file.type.startsWith("video/") ? "video" : "image");
  };

  const handleCreateAd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!brandName.trim() || !adText.trim()) return;
    setIsUploading(true);

    let finalMediaUrl = null;

    try {
      if (mediaFile) {
        const storage = getStorage();
        const fileExt = mediaFile.name.split(".").pop();
        const fileName = `ads/${Date.now()}_ad.${fileExt}`;
        const storageRef = ref(storage, fileName);

        await uploadBytes(storageRef, mediaFile);
        finalMediaUrl = await getDownloadURL(storageRef);
      }

      await addDoc(collection(db, "posts"), {
        type: "ad",
        isSponsored: true,
        authorName: brandName.trim(),
        authorPhoto: null,
        text: adText.trim(),
        mediaUrl: finalMediaUrl,
        mediaType: mediaType,
        link: targetLink.trim() || null,
        actionText: actionText.trim() || "Перейти",
        likes: [],
        comments: [],
        commentsCount: 0,
        repostsCount: 0,
        createdAt: serverTimestamp(),
      });

      setBrandName("");
      setAdText("");
      setTargetLink("");
      setActionText("Замовити зараз");
      setMediaFile(null);
      setMediaPreview(null);
      setMediaType(null);
      alert("Рекламу успішно опубліковано у Стрічці!");
    } catch (err) {
      console.error(err);
      alert("Помилка публікації реклами.");
    } finally {
      setIsUploading(false);
    }
  };

  // 🚀 МАГІЯ ДЛЯ ЗАВАНТАЖЕННЯ QR-КОДУ У ВИСОКІЙ ЯКОСТІ
  const downloadQR = (petId: string, petName: string) => {
    const canvas = document.getElementById(`qr-canvas-${petId}`) as HTMLCanvasElement;
    if (!canvas) return alert("Помилка генерації QR");
    
    const pngUrl = canvas.toDataURL("image/png");
    const downloadLink = document.createElement("a");
    downloadLink.href = pngUrl;
    downloadLink.download = `QR_${petName.replace(/\s+/g, '_')}_${petId.slice(0,4)}.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  return (
    <div className="flex flex-col h-[100dvh] absolute inset-0 z-[700] bg-[var(--bg-soft)] animate-fade-in pb-[80px]">
      <div className="bg-[var(--bg-card)] border-b border-[var(--border-color)] px-5 py-4 shrink-0 pt-12 shadow-sm flex flex-col gap-4 relative z-30">
        <div className="flex items-center gap-3">
          {onClose && (
            <button
              onClick={onClose}
              className="text-[var(--text-main)] active:scale-95 mr-2"
            >
              <ArrowLeft size={24} />
            </button>
          )}
          <Bug size={24} className="text-[#a073e7]" />
          <h2 className="font-extrabold text-xl text-[var(--text-main)]">
            Панель Розробника
          </h2>
        </div>

        <div className="flex gap-2 bg-[var(--bg-soft)] p-1 rounded-xl border border-[var(--border-color)]">
          <button
            onClick={() => setActiveTab("orders")}
            className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 ${
              activeTab === "orders"
                ? "bg-[var(--bg-card)] text-[#a073e7] shadow-sm"
                : "text-[var(--text-muted)]"
            }`}
          >
            <Package size={16} /> Замовлення{" "}
            {orders.filter((o) => o.status !== "completed").length > 0 && (
              <span className="bg-[var(--red-text)] text-white px-1.5 py-0.5 rounded-full text-[9px]">
                {orders.filter((o) => o.status !== "completed").length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("bugs")}
            className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 ${
              activeTab === "bugs"
                ? "bg-[var(--bg-card)] text-[#a073e7] shadow-sm"
                : "text-[var(--text-muted)]"
            }`}
          >
            <MessageSquare size={16} /> Відгуки
          </button>
          <button
            onClick={() => setActiveTab("ads")}
            className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 ${
              activeTab === "ads"
                ? "bg-[var(--bg-card)] text-[#a073e7] shadow-sm"
                : "text-[var(--text-muted)]"
            }`}
          >
            <Megaphone size={16} /> Реклама
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4 no-scrollbar">
        {activeTab === "orders" && (
          <>
            {orders.length === 0 && (
              <div className="text-center py-10 text-[var(--text-muted)] font-bold text-sm">
                Замовлень поки немає
              </div>
            )}
            {orders.map((order) => (
              <div
                key={order.id}
                className={`p-4 rounded-2xl border shadow-sm relative animate-slide-up ${
                  order.status === "completed"
                    ? "bg-[var(--bg-main)] opacity-50 border-[var(--border-color)]"
                    : "bg-[var(--bg-card)] border-[#a073e7]"
                }`}
              >
                <div className="flex justify-between items-start mb-2">
                  <span className="text-[10px] font-bold text-white bg-[#a073e7] px-2 py-1 rounded-md">
                    ID: {order.id.slice(0, 6)}
                  </span>
                  <span className="text-[10px] text-[var(--text-muted)]">
                    {order.createdAt?.toDate?.().toLocaleString()}
                  </span>
                </div>
                <div className="flex flex-col gap-2 mt-3">
                  <div className="flex items-center gap-2 text-sm text-[var(--text-main)]">
                    <User size={16} className="text-[#a073e7]" />{" "}
                    <b>Власник:</b> {order.ownerName}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[var(--text-main)]">
                    <Phone size={16} className="text-[#a073e7]" />{" "}
                    <b>Телефон:</b> {order.ownerPhone}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[var(--text-main)]">
                    <Package size={16} className="text-[#a073e7]" />{" "}
                    <b>Песик:</b> {order.petName} (ID:{" "}
                    {order.petId ? String(order.petId).slice(0, 4) : ""})
                  </div>
                  <div className="flex items-start gap-2 text-sm text-[var(--text-main)]">
                    <MapPin
                      size={16}
                      className="text-[#a073e7] mt-0.5 shrink-0"
                    />{" "}
                    <b>Відправка:</b>{" "}
                    <span className="flex-1">{order.deliveryData}</span>
                  </div>
                </div>

                {/* 🚀 ПРИХОВАНИЙ CANVAS ТА КНОПКА ЗАВАНТАЖЕННЯ */}
                {order.petId && (
                  <div className="mt-4 pt-4 border-t border-[var(--border-color)] flex flex-col gap-3">
                    <div style={{ display: "none" }}>
                      <QRCodeCanvas
                        id={`qr-canvas-${order.petId}`}
                        value={`${window.location.origin}/?scan=${order.petId}`}
                        size={1024} // Висока роздільна здатність для друку!
                        level={"H"} // Максимальна корекція помилок
                        fgColor="#2d1f45"
                      />
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => downloadQR(order.petId, order.petName)}
                        className="flex-1 py-3 bg-[#a073e7] text-white font-bold rounded-xl flex justify-center items-center gap-2 active:scale-95 transition-all text-xs"
                      >
                        <Download size={16} /> Завантажити QR
                      </button>

                      {order.status !== "completed" && (
                        <button
                          onClick={() => markOrderAsDone(order.id)}
                          className="flex-1 py-3 bg-[#10b981] text-white font-bold rounded-xl flex justify-center items-center gap-2 active:scale-95 transition-all text-xs"
                        >
                          <CheckCircle size={16} /> Відправлено
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {activeTab === "bugs" && (
          <>
            {bugs.length === 0 && (
              <div className="text-center py-10 text-[var(--text-muted)] font-bold text-sm">
                Багів та відгуків немає
              </div>
            )}
            {bugs.map((bug) => (
              <div
                key={bug.id}
                className="p-4 rounded-2xl border border-[var(--border-color)] bg-[var(--bg-card)] shadow-sm relative animate-slide-up"
              >
                <div className="flex justify-between items-start mb-2">
                  <span className="text-[10px] text-[var(--text-muted)] font-bold">
                    {bug.createdAt?.toDate?.().toLocaleString()}
                  </span>
                  <button
                    onClick={() => deleteBug(bug.id)}
                    className="text-[var(--text-muted)] hover:text-[var(--red-text)] transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                {bug.authorName && (
                  <p className="text-xs font-bold text-[var(--text-main)] mb-1">
                    Від: {bug.authorName}
                  </p>
                )}
                <p className="text-sm text-[var(--text-main)] whitespace-pre-wrap">
                  {bug.text || bug.description || "Без тексту"}
                </p>
              </div>
            ))}
          </>
        )}

        {activeTab === "ads" && (
          <div className="flex flex-col gap-6 animate-fade-in">
            <form
              onSubmit={handleCreateAd}
              className="bg-[var(--bg-card)] p-5 rounded-2xl border border-[var(--border-color)] shadow-sm flex flex-col gap-4"
            >
              <h3 className="font-extrabold text-sm text-[#a073e7] uppercase tracking-wider flex items-center gap-2">
                <Megaphone size={16} /> Нова рекламна кампанія
              </h3>

              <div>
                <label className="text-xs font-bold text-[var(--text-muted)] uppercase block mb-1.5">
                  Назва бренду / Рекламодавця
                </label>
                <input
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  required
                  placeholder="Напр. Клуб Чотири Лапи"
                  className="w-full h-11 px-4 bg-[var(--bg-soft)] border border-[var(--border-color)] text-[var(--text-main)] rounded-xl outline-none text-sm"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-[var(--text-muted)] uppercase block mb-1.5">
                  Текст рекламного оголошення
                </label>
                <textarea
                  value={adText}
                  onChange={(e) => setAdText(e.target.value)}
                  required
                  placeholder="Введіть смачний рекламний опис товару чи сервісу..."
                  className="w-full p-3 bg-[var(--bg-soft)] border border-[var(--border-color)] text-[var(--text-main)] rounded-xl outline-none text-sm min-h-[80px] resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-[var(--text-muted)] uppercase block mb-1.5">
                    Посилання (URL)
                  </label>
                  <input
                    value={targetLink}
                    onChange={(e) => setTargetLink(e.target.value)}
                    placeholder="https://..."
                    className="w-full h-11 px-3 bg-[var(--bg-soft)] border border-[var(--border-color)] text-[var(--text-main)] rounded-xl outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-[var(--text-muted)] uppercase block mb-1.5">
                    Текст на кнопці
                  </label>
                  <input
                    value={actionText}
                    onChange={(e) => setActionText(e.target.value)}
                    placeholder="Замовити зараз"
                    className="w-full h-11 px-3 bg-[var(--bg-soft)] border border-[var(--border-color)] text-[var(--text-main)] rounded-xl outline-none text-sm"
                  />
                </div>
              </div>

              <div className="border-2 border-dashed border-[var(--border-color)] rounded-xl p-4 flex flex-col items-center justify-center bg-[var(--bg-soft)] relative">
                {mediaPreview ? (
                  <div className="relative w-full max-h-[150px] overflow-hidden rounded-lg flex justify-center bg-black/5">
                    {mediaType === "image" ? (
                      <img
                        src={mediaPreview}
                        className="max-h-[150px] object-contain"
                      />
                    ) : (
                      <video
                        src={mediaPreview}
                        controls
                        className="max-h-[150px]"
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setMediaFile(null);
                        setMediaPreview(null);
                        setMediaType(null);
                      }}
                      className="absolute top-1 right-1 bg-black/60 p-1 rounded-full text-white"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex flex-col items-center text-[var(--text-muted)] gap-1 active:scale-95 transition-all"
                  >
                    <ImagePlus size={24} className="text-[#a073e7]" />
                    <span className="text-xs font-bold">
                      Додати фото або відео банера
                    </span>
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  className="hidden"
                  onChange={handleMediaChange}
                />
              </div>

              <button
                type="submit"
                disabled={isUploading}
                className="w-full h-12 bg-[#a073e7] text-white font-bold rounded-xl flex justify-center items-center gap-2 active:scale-95 transition-all text-sm shadow-md disabled:opacity-50"
              >
                {isUploading ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Plus size={18} />
                )}
                {isUploading
                  ? "Завантаження у хмару..."
                  : "Запустити рекламу в Стрічку"}
              </button>
            </form>

            <div className="flex flex-col gap-3">
              <h4 className="font-extrabold text-xs text-[var(--text-muted)] uppercase tracking-wider px-1">
                Активна реклама ({ads.length})
              </h4>
              {ads.length === 0 && (
                <div className="text-center py-6 bg-[var(--bg-card)] border border-[var(--border-color)] rounded-2xl text-xs font-bold text-[var(--text-muted)]">
                  Зараз немає запущеної реклами
                </div>
              )}
              {ads.map((ad) => (
                <div
                  key={ad.id}
                  className="bg-[var(--bg-card)] border border-[var(--border-color)] p-4 rounded-xl shadow-sm flex items-center justify-between gap-3 animate-slide-up"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] bg-[var(--accent-soft)] text-[#a073e7] font-black px-1.5 py-0.5 rounded uppercase">
                        Ad
                      </span>
                      <h5 className="font-bold text-sm text-[var(--text-main)] truncate">
                        {ad.authorName}
                      </h5>
                    </div>
                    <p className="text-xs text-[var(--text-muted)] truncate mt-1">
                      {ad.text}
                    </p>
                  </div>
                  <button
                    onClick={() => deleteAd(ad.id)}
                    className="p-2.5 bg-[var(--bg-soft)] text-[var(--text-muted)] hover:text-[var(--red-text)] rounded-xl transition-colors shrink-0"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}